// Nedo store locator (section nedo-store-locator): search, city buttons, expanding and collapsing the list.
(() => {
  if (window.customElements.get('nedo-store-locator')) {
    return;
  }

  // Lowercase, no diacritics, single spaces ("Łódź" -> "lodz")
  const normalize = (text) =>
    Array.from(String(text || '').toLowerCase().normalize('NFD'))
      .filter((char) => {
        const code = char.charCodeAt(0);
        return code < 0x300 || code > 0x36f;
      })
      .join('')
      .split('ł')
      .join('l')
      .split(' ')
      .filter(Boolean)
      .join(' ');

  // Plural forms for 1, 2-4 (except 12-14) and 5+, e.g. "strefa relaksu, strefy relaksu, stref relaksu"
  const pluralize = (count, formsText) => {
    const forms = String(formsText || '').split(',').map((form) => form.trim());
    const mod10 = count % 10;
    const mod100 = count % 100;
    let index = 2;
    if (count === 1) {
      index = 0;
    } else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
      index = 1;
    }
    return `${count} ${forms[index] || forms[forms.length - 1] || ''}`.trim();
  };

  class NedoStoreLocator extends HTMLElement {
    connectedCallback() {
      if (this._initialized) {
        return;
      }
      this._initialized = true;

      this.items = Array.from(this.querySelectorAll('.nedo-locator__item'));
      this.items.forEach((item) => {
        item._search = normalize(item.dataset.search || item.textContent);
        item._city = normalize(item.dataset.city);
      });

      this.searchForm = this.querySelector('.nedo-locator__search');
      this.searchInput = this.querySelector('input[type="search"]');
      this.searchButton = this.searchForm ? this.searchForm.querySelector('button') : null;
      this.chips = Array.from(this.querySelectorAll('.nedo-locator__chip'));
      this.listToggle = this.querySelector('.nedo-locator__toggle');
      this.citiesToggle = this.querySelector('.nedo-locator__cities-toggle');
      this.resultsHeader = this.querySelector('.nedo-locator__results-header');
      this.titleElement = this.querySelector('.nedo-locator__title');
      this.summaryElement = this.querySelector('.nedo-locator__summary');
      this.emptyState = this.querySelector('.nedo-locator__empty');
      this.selectedCity = null;

      // Initial texts from the HTML, restored after searching and collapsing
      this._titleAll = this.titleElement ? this.titleElement.textContent : '';
      this._summaryAll = this.summaryElement ? this.summaryElement.textContent : '';
      this._viewAllText = this.listToggle ? this.getToggleText(this.listToggle) : '';
      this._allCitiesText = this.citiesToggle ? this.getToggleText(this.citiesToggle) : '';

      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => {
          this.selectCity(null);
          this.filter();
        });
      }

      // The list already filters while typing, so the search button and Enter only scroll to the results.
      // The button is type="button" (Impact shows a never-ending loader on submit buttons); Enter in the field still submits the form.
      if (this.searchForm) {
        this.searchForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.showResults();
        });
      }

      if (this.searchButton) {
        this.searchButton.addEventListener('click', () => this.showResults());
      }

      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          const city = normalize(chip.dataset.city) || null;
          this.selectCity(this.selectedCity === city ? null : city);
          if (this.searchInput) {
            this.searchInput.value = '';
          }
          this.filter();
        });
      });

      if (this.listToggle) {
        this.listToggle.addEventListener('click', () => {
          const expand = this.hasAttribute('data-collapsed');
          this.toggleAttribute('data-collapsed', !expand);
          this.listToggle.setAttribute('aria-expanded', expand ? 'true' : 'false');
          this.setToggleText(this.listToggle, expand ? this.dataset.viewLess : this._viewAllText);
          this.markCityGroups();
          if (!expand) {
            this.scrollToResults(true);
          }
        });
      }

      if (this.citiesToggle) {
        this.citiesToggle.addEventListener('click', () => {
          const expand = !this.hasAttribute('data-cities-open');
          this.toggleAttribute('data-cities-open', expand);
          this.citiesToggle.setAttribute('aria-expanded', expand ? 'true' : 'false');
          this.setToggleText(this.citiesToggle, expand ? this.dataset.citiesLess : this._allCitiesText);
        });
      }

      const clearButton = this.querySelector('.nedo-locator__clear');
      if (clearButton) {
        clearButton.addEventListener('click', () => {
          this.selectCity(null);
          if (this.searchInput) {
            this.searchInput.value = '';
            this.searchInput.focus();
          }
          this.filter();
        });
      }

      // Prefiltered link, e.g. /pages/znajdz-nasze-fotele?miasto=Gdańsk
      const cityParam = new URLSearchParams(window.location.search).get('miasto');
      if (cityParam) {
        const city = normalize(cityParam);
        const chip = this.chips.find((element) => element.dataset.city && normalize(element.dataset.city) === city);
        if (chip) {
          this.selectCity(city);
        } else if (this.searchInput) {
          this.selectCity(null);
          this.searchInput.value = cityParam;
        }
        this.filter();
      }
    }

    getToggleText(button) {
      const label = button.querySelector('.nedo-locator__toggle-text');
      return label ? label.textContent : '';
    }

    setToggleText(button, text) {
      const label = button.querySelector('.nedo-locator__toggle-text');
      if (label && text) {
        label.textContent = text;
      }
    }

    showResults() {
      this.filter();
      if (this.searchInput) {
        this.searchInput.blur();
      }
      this.scrollToResults();
    }

    // onlyIfAbove: scroll only when the top of the results is above the viewport (after collapsing)
    scrollToResults(onlyIfAbove) {
      if (!this.resultsHeader) {
        return;
      }
      if (!onlyIfAbove || this.resultsHeader.getBoundingClientRect().top < 0) {
        this.resultsHeader.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    // null means "all cities"
    selectCity(city) {
      this.selectedCity = city;
      const noFilter = city === null && !(this.searchInput && this.searchInput.value.trim());
      this.chips.forEach((chip) => {
        const isAllChip = !chip.dataset.city;
        const active = isAllChip ? noFilter : city !== null && normalize(chip.dataset.city) === city;
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
        chip.classList.toggle('button--outline', !active);
      });
    }

    // On desktop the city name is shown only in the first visible row of each city
    markCityGroups() {
      const collapsed = this.hasAttribute('data-collapsed') && !this.hasAttribute('data-filtering');
      let previousCity = null;
      this.items.forEach((item) => {
        if (item.hidden || (collapsed && item.classList.contains('is-extra'))) {
          return;
        }
        item.classList.toggle('is-city-start', item._city !== previousCity);
        previousCity = item._city;
      });
    }

    filter() {
      const query = normalize(this.searchInput ? this.searchInput.value : '');
      const words = query ? query.split(' ') : [];
      const filtering = Boolean(this.selectedCity || words.length);
      const cities = new Set();
      let found = 0;

      this.items.forEach((item) => {
        let visible = true;
        if (this.selectedCity) {
          visible = item._city === this.selectedCity;
        } else if (words.length) {
          visible = words.every((word) => item._search.includes(word));
        }
        item.hidden = !visible;
        if (visible) {
          found += 1;
          cities.add(item._city);
        }
      });

      this.toggleAttribute('data-filtering', filtering);
      this.selectCity(this.selectedCity);

      if (this.titleElement) {
        this.titleElement.textContent = filtering ? this.dataset.titleResults || this._titleAll : this._titleAll;
      }

      if (this.summaryElement) {
        this.summaryElement.textContent = !filtering
          ? this._summaryAll
          : found === 0
            ? ''
            : (this.dataset.summary || '')
                .replace('[zones]', pluralize(found, this.dataset.zoneForms))
                .replace('[cities]', pluralize(cities.size, this.dataset.cityForms));
      }

      if (this.emptyState) {
        this.emptyState.hidden = !(filtering && found === 0);
      }

      this.markCityGroups();
    }
  }

  window.customElements.define('nedo-store-locator', NedoStoreLocator);
})();